import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { useRillContext } from "../rill/RillReact/share/base";
import {
  clearInsertUndo,
  INSERT_UNDO_EVENT,
  readInsertUndo,
} from "../utils/insert-undo";

/** How long the Undo toast stays up. */
const TOAST_MS = 5000;
/** How long the new rows stay highlighted. */
const HIGHLIGHT_MS = 2500;

/**
 * After an insert above/below an exercise: briefly highlight the new rows and
 * offer a 5 s Undo (no confirmation). The insert actions leave a snapshot via
 * saveInsertUndo; this reads it on the editor's next load.
 *
 * @param {object} props
 * @param {string} [props.cycleId] program editor: only offered on the program the insert was made in
 * @param {string} [props.athleteId] calendar: only offered on that athlete's calendar
 */
const InsertUndoToast = ({ cycleId, athleteId }) => {
  const { t } = useTranslation("athlete-portal");
  const {
    locals: { query },
  } = useRillContext();
  // The insert modal stays open ("Saving…") while the API answers; the toast
  // and highlight would run out behind it, so wait until it has closed.
  const modalOpen = Boolean(query?.modal?.type);
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    if (modalOpen) return undefined;

    let timers = [];
    let rows = [];

    const reset = () => {
      for (const timer of timers) clearTimeout(timer);
      timers = [];
      for (const row of rows) row.classList.remove("insert-highlight");
      rows = [];
    };

    const show = () => {
      const found = readInsertUndo();
      const { snapshot } = found || {};
      const isHere = snapshot?.day
        ? Boolean(athleteId) && String(snapshot.day.user) === String(athleteId)
        : Boolean(cycleId) && String(snapshot?.cycle?.id) === String(cycleId);
      if (!found || !isHere) return;
      reset();
      setEntry(found);

      // The new rows render after the redirect, so look for them for a moment.
      const ids = found.snapshot.exercises || [];
      let tries = 0;
      const find = () => {
        const present = ids
          .map((id) => document.getElementById(id))
          .filter(Boolean);
        if (present.length < ids.length && tries++ < 20) {
          timers.push(setTimeout(find, 100));
          return;
        }
        rows = present;
        for (const row of rows) row.classList.add("insert-highlight");
        rows[0]?.scrollIntoView?.({ block: "center", behavior: "smooth" });
        timers.push(
          setTimeout(() => {
            for (const row of rows) row.classList.remove("insert-highlight");
          }, HIGHLIGHT_MS)
        );
      };
      find();

      timers.push(
        setTimeout(() => {
          clearInsertUndo();
          setEntry(null);
        }, TOAST_MS)
      );
    };

    show();
    window.addEventListener(INSERT_UNDO_EVENT, show);
    return () => {
      window.removeEventListener(INSERT_UNDO_EVENT, show);
      reset();
      setEntry(null);
    };
  }, [cycleId, athleteId, modalOpen]);

  if (!entry) return null;

  return (
    <output className="insert-undo-toast" aria-live="polite">
      <span>{t("programs.insert.toastInserted", { count: entry.count })}</span>
      <button
        type="button"
        data-button-type="text"
        onClick={() => {
          const { snapshot } = entry;
          clearInsertUndo();
          setEntry(null);
          submit("/actions/exercise/insert-undo", {
            method: "POST",
            body: { snapshot },
          });
        }}
      >
        {t("programs.insert.undo")}
      </button>
      <span
        className="insert-undo-bar"
        style={{ animationDuration: `${TOAST_MS}ms` }}
      />
    </output>
  );
};

export default InsertUndoToast;
